/**
 * DiscoverVariant — Discover/Trending card.
 * Uses shared EngagementBar + ShareSection.
 * Unique: Reason badges, promoted tag, compact media.
 */
import { useState, useMemo, useCallback, memo } from 'react';
import { MapPin, TrendingUp, Users, Star, Sparkles, Award, UtensilsCrossed } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useFoodlist } from '../../contexts/FoodlistContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';
import AuthModal from '../AuthModal';
import EngagementBar from './EngagementBar';
import ShareSection from './ShareSection';
import { buildShareHandler, DirectionsPlaceholder, API_URL } from './shared';
import { useFoodImage } from '../../hooks/useFoodImage';
import { useEngagement } from '../../hooks/useEngagement';

const REASON_ICONS = { 'Trending': TrendingUp, 'follow': Users, 'Matches': Star, 'Promoted': Sparkles };

const DiscoverVariant = ({ post, onSaveToFoodlist }) => {
  const { src: imgSrc, source: imgSource, attribution: imgAttribution, handleError: handleImgError } = useFoodImage(post.caption || post.venue_name, post.image_url || post.thumb_url, post.venue_name || '');
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isInFoodlist, toggleFoodlist } = useFoodlist();
  const { toast } = useToast();

  const ReasonIcon = Object.entries(REASON_ICONS).find(([k]) => post.reason?.includes(k))?.[1];
  const tags = (post.tags || []).slice(0, 3);
  const insight = post.reason && post.reason !== 'Promoted' ? post.reason : (post.notes || '');

  const { repostCount, reposted, shareCount, toggleRepost, recordShare } = useEngagement(
    post.caption || '', post.venue_name || '', post.post_id || '', user?.user_id || ''
  );
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [saveAnim, setSaveAnim] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [claimLoading, setClaimLoading] = useState(false);
  const [vibeDisliked, setVibeDisliked] = useState(false);

  const foodlistItem = useMemo(() => ({ name: post.venue_name || post.caption || 'Dish', restaurant: post.venue_name || '', location: post.city || '', image: post.image_url || '', cuisine: post.cuisine || '' }), [post]);
  const inFoodlist = isInFoodlist(foodlistItem.name, foodlistItem.restaurant);
  const [foodlistCount, setFoodlistCount] = useState(post.saves_count || 0);

  const testId = useMemo(() => (post.post_id || post.caption || 'item').toString().toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 30), [post.post_id, post.caption]);

  const handleRepost = useCallback(() => {
    if (!user) { setShowAuthModal(true); return; }
    toggleRepost();
  }, [user, toggleRepost]);

  const handleFoodlistToggle = useCallback(() => {
    if (!user) { setShowAuthModal(true); return; }
    toggleFoodlist(foodlistItem);
    if (!inFoodlist) { setSaveAnim(true); setFoodlistCount(c => c + 1); setTimeout(() => setSaveAnim(false), 800); }
    else { setFoodlistCount(c => Math.max(0, c - 1)); }
    onSaveToFoodlist?.(post);
  }, [user, foodlistItem, toggleFoodlist, inFoodlist, onSaveToFoodlist, post]);

  const handleFoodCardTap = useCallback(() => {
    if (!user) { setShowAuthModal(true); return; }
    navigate('/create-foodcard', { state: { prefill: { name: post.caption, restaurant: post.venue_name, cuisine: post.cuisine, image: post.image_url }, claimMode: true } });
  }, [user, post, navigate]);

  const handleNotMyVibe = useCallback(async () => {
    if (!user) { setShowAuthModal(true); return; }
    setVibeDisliked(true);
    try {
      await fetch(`${API_URL}/api/preferences/not-my-vibe`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ item_name: post.caption || '', cuisine: post.cuisine || '', category: 'discover', reason: 'not_interested', source: 'discover' }),
      });
    } catch {}
  }, [user, post]);

  const shareUrl = `${window.location.origin}/foodie?post=${post.post_id || ''}`;
  const baseShareHandler = useMemo(() => buildShareHandler(post.caption, post.venue_name, shareUrl), [post.caption, post.venue_name, shareUrl]);
  const handleSocialShare = useCallback(async (platform) => { await baseShareHandler(platform); recordShare(platform); if (platform === 'copy') toast({ title: 'Link copied!' }); }, [baseShareHandler, recordShare, toast]);

  return (
    <>
      <article className="group bg-white rounded-[22px] overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.08)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] transition-all duration-300" data-testid={`discover-card-${testId}`}>
        {/* Media */}
        {imgSrc ? (
          <div className="relative h-52 overflow-hidden bg-gray-100 group">
            <img src={imgSrc} alt={post.caption} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" onError={handleImgError} loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
            {imgAttribution && <div className="absolute bottom-12 right-3 bg-black/[0.32] backdrop-blur-md border border-white/[0.12] px-2 py-0.5 rounded-full"><span className="text-white/90 text-[10px] font-medium">{imgAttribution}</span></div>}
            {claimed && <div className="absolute top-3 right-3 bg-emerald-500/90 backdrop-blur-md px-2.5 py-1 rounded-full flex items-center gap-1"><Award className="w-3 h-3 text-white" /><span className="text-white text-[10px] font-bold">Collected</span></div>}
            {post.reason && <div className="absolute top-3 left-3 flex items-center gap-1 bg-black/55 backdrop-blur-sm text-white text-[10px] font-semibold px-2.5 py-1 rounded-full">{ReasonIcon && <ReasonIcon className="w-2.5 h-2.5" />}{post.reason}</div>}
            {post.is_promoted && <div className="absolute top-3 right-3 bg-[#F4C430] text-gray-900 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1"><Sparkles className="w-2.5 h-2.5" />Sponsored</div>}
            <div className="absolute bottom-0 left-0 right-0 px-3 pb-3 pt-6">
              <p className="text-white font-bold text-sm leading-snug line-clamp-2">{post.caption}</p>
              {(post.venue_name || post.location) && <p className="text-white/70 text-[10px] flex items-center gap-1 mt-0.5 truncate"><MapPin className="w-2.5 h-2.5 flex-shrink-0" />{[post.venue_name, post.location].filter(Boolean).join(' · ')}</p>}
            </div>
          </div>
        ) : (
          <DirectionsPlaceholder
            name={post.venue_name || post.caption}
            location={post.location}
            height="h-52"
            testId={`discover-${testId}`}
          />
        )}

        <div className="p-4 md:p-5 space-y-2">
          <div className="flex items-center gap-2 text-[11px] text-gray-400 flex-wrap">
            {post.cuisine && <span className="flex items-center gap-0.5"><UtensilsCrossed className="w-2.5 h-2.5 text-gray-400" /> {post.cuisine}</span>}
            {post.vibe && <><span className="opacity-30">·</span><span>{post.vibe}</span></>}
            {post.city && <><span className="opacity-30">·</span><span className="flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5 text-[#FF6B35]" />{post.city}</span></>}
          </div>
          {insight && <p className="text-[11px] text-gray-500 italic leading-relaxed line-clamp-2">{insight}</p>}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {tags.map(t => <button key={t} data-testid={`discover-tag-pill-${t}`} onClick={() => navigate(`/foodie?tag=${encodeURIComponent(t)}`)} className="text-[10px] bg-[#FFF5F0] text-[#FF6B35] px-2 py-0.5 rounded-full font-medium hover:bg-[#FF6B35] hover:text-white transition-colors cursor-pointer">#{t}</button>)}
            </div>
          )}

          <EngagementBar repostCount={repostCount} onRepost={handleRepost} reposted={reposted}
            onNotMyVibe={handleNotMyVibe} vibeDisliked={vibeDisliked}
            inFoodlist={inFoodlist} foodlistCount={foodlistCount} onFoodlistToggle={handleFoodlistToggle} saveAnim={saveAnim}
            onFoodCardTap={handleFoodCardTap} claimed={claimed} claimLoading={claimLoading} testId={testId} />
          <ShareSection onSocialShare={handleSocialShare} shareCount={shareCount} testId={testId} compact />
        </div>
      </article>
      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
    </>
  );
};

const MemoizedDiscoverVariant = memo(DiscoverVariant);
MemoizedDiscoverVariant.displayName = 'DiscoverVariant';
export default MemoizedDiscoverVariant;
